import { queryOptions } from '@tanstack/react-query'
import { apiGet, apiPost } from './client'

/**
 * IT Audit - constatarile: registrul, mutarea unei constatari, scrisul de
 * mana peste raport si solutii. Perechile vechi, din `settings/Audit`:
 *
 *   register  -> GET  /settings/audit/register?all=1
 *   finding   -> POST /settings/audit/finding   (fingerprint, state, owner, due_on, why)
 *   write     -> POST /settings/audit/write     (run, what, text, restore)
 *
 * Calea NU poarta `/api/v2`: il pune `apiGet`/`apiPost`.
 *
 * Randurile sunt CHIAR cele ale portalului (`Services\Audit\Findings`), cu
 * aceleasi nume de coloane: v4 nu are alta definitie a unei constatari.
 */

/** Severitatile manualului, in ordinea in care conteaza (`Findings::SEVERITIES`). */
export const SEVERITIES = ['Critical', 'High', 'Medium', 'Low'] as const

export type Severity = (typeof SEVERITIES)[number]

/** Ce se poate intampla cu o constatare dupa ce a fost scrisa (`Findings::STATES`). */
export const FINDING_STATES = ['open', 'in progress', 'fixed', 'risk accepted', 'not a finding'] as const

export type FindingState = (typeof FINDING_STATES)[number]

/** Un rand din `it_audit_findings`, asa cum il da `Findings::forRun` / `register`. */
export type AuditFinding = {
  ID: number
  run_id: number
  step_id: number
  company_id: number
  type_id: number
  area_code: string
  side: string
  check_id: string
  title: string
  severity: string
  evidence: string
  machines: string
  fingerprint: string
  due_on: string
  state: string
  owner: string
  moved_why: string
  moved_at: string | null
  created_at: string
}

/** Un rand din registru: acelasi rand, cu prima vedere si ceasul socotit. */
export type RegisterRow = AuditFinding & {
  first_seen: string
  overdue: boolean
  days_open: number
}

export type FindingCounts = {
  Critical: number
  High: number
  Medium: number
  Low: number
  serious: number
}

export type AuditRegister = {
  /** Versiunea livrarii - `?v=` pe foile portalului. */
  version: string
  /** Tabela exista pe instanta? Fara ea nu e nimic de aratat, si se spune. */
  ready: boolean
  openOnly: boolean
  rows: RegisterRow[]
  /** Numai cand `ready`. */
  counts: FindingCounts | null
  overdue: number
  states: string[]
  /** `settings.audit.edit`: cine poate muta o constatare. */
  mayRun: boolean
}

export function auditRegisterQuery(all: boolean) {
  return queryOptions({
    queryKey: ['settings', 'audit', 'register', all ? 'all' : 'open'],
    queryFn: () => apiGet<AuditRegister>(`/settings/audit/register${all ? '?all=1' : ''}`),
  })
}

export type FindingChange = {
  state?: string
  owner?: string
  due_on?: string
  /** Obligatoriu cand se muta `due_on`: o amanare fara motiv nu se scrie. */
  why?: string
}

export type FindingMoved = { ok: true } | { ok: false; error: string }

/** `Audit::finding` - starea, cine raspunde, termenul. Un raspuns cu `ok: false` e un refuz spus, nu o eroare. */
export function moveFinding(fingerprint: string, change: FindingChange): Promise<FindingMoved> {
  return apiPost<FindingMoved>('/settings/audit/finding', {
    fingerprint,
    state: change.state ?? '',
    owner: change.owner ?? '',
    due_on: change.due_on ?? '',
    why: change.why ?? '',
  })
}

/** Ce se poate scrie de mana: raportul si solutiile (`Runs::editable`). Capitolele, nu. */
export type Written = 'report' | 'solutions'

export type WriteAnswer =
  | {
      ok: true
      /** Textul randat, acelasi subset ca in fereastra agentului. */
      html: string
      /** "Drafted by the agent, then edited by ... on ..." - `Runs::hand`. */
      hand: string
      /** Textul brut, cel care se editeaza. */
      text: string
    }
  | { ok: false; error: string }

/** `Audit::write` - scrie raportul sau solutiile de mana; `restore` intoarce ciorna agentului. */
export function writeRun(run: number, what: Written, text: string, restore = false): Promise<WriteAnswer> {
  return apiPost<WriteAnswer>('/settings/audit/write', {
    run,
    what,
    restore: restore ? '1' : '0',
    text,
  })
}
